"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { useLocaleStore, type Locale } from "@/store/locale.store";
import { useTranslation } from "@/lib/i18n";
import { getFeaturedCities } from "@/lib/cities";
import { FaFacebookF, FaInstagram, FaTiktok, FaLinkedinIn } from "react-icons/fa6";

function AppStoreButton({ lang }: { lang: Locale }) {
  const src = lang === "fr" ? "/badges/AppStore-Badge-FR.svg" : "/badges/AppStore-Badge-EN.svg";
  return (
    <a href="https://apps.apple.com/dz/app/fast-livraison-repas-courses/id1469020170?l=fr-FR" target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store" className="hover:opacity-80 transition-opacity">

      <Image  src={src} alt="Download on the App Store" className="block h-[50px] w-[160px] md:h-[50px] md:w-[160px]" width={160} height={50} />
    </a>
  );
}

function GooglePlayButton({ lang }: { lang: Locale }) {
  const src = lang === "fr" ? "/badges/GooglePlay-Badge-FR.svg" : "/badges/GooglePlay-Badge-EN.svg";
  return (
    <a href="https://play.google.com/store/apps/details?id=com.customer.fastdelivery&hl=fr" target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play" className="hover:opacity-80 transition-opacity">

      <Image src={src} alt="Get it on Google Play" className="block h-[50px] w-[160px] md:h-[50px] md:w-[160px]" width={160} height={50} />
    </a>
  );
}

export function Footer() {
  const { locale } = useLocaleStore();
  const t = useTranslation();
  const pathname = usePathname();

  const COLUMNS = [
    {
      title: t.footer.aboutUs,
      links: [
        // href="#" on four links left the brand with no entity anchor at all.
        // Only pages that exist are linked; Blog and Presse are dropped until
        // there is something behind them.
        { label: t.footer.about, href: "/a-propos" },
        {
          label: t.footer.careers,
          href: "https://airtable.com/appRgIpNYmxzW7FXt/shr82KDhaNfW4H8QF",
        },
      ],
    },
    {
      title: t.footer.help,
      links: [
        // /assistant is indexable and submitted in the sitemap but was linked
        // from nowhere — an orphan Google would crawl once and forget.
        { label: t.footer.deliveryHelp, href: "/aide/livraison" },
        { label: t.footer.assistant, href: "/assistant" },
        { label: t.footer.accountDetails, href: "/account" },
        { label: t.footer.orderHistory, href: "/orders" },
        { label: t.footer.support, href: "https://help.fastapp.dz/" },
      ],
    },
    {
      title: t.footer.partners,
      links: [
        { label: t.footer.becomePartner, href: "https://go.fastapp.dz/devenir-partenaire" },
        { label: t.footer.becomeCourier, href: "https://go.fastapp.dz/devenir-partenaire" },
      ],
    },
    {
      title: t.footer.byCity,
      links: [
        { label: t.footer.allCities, href: "/livraison" },
        { label: t.footer.brands, href: "/enseignes" },
        ...getFeaturedCities().map((c) => ({ label: c.name, href: `/livraison/${c.slug}` })),
      ],
    },
  ];

  // Les quatre liens légaux étaient morts sur les 700 pages du site :
  // `cgu.fastapp.dz` et `privacy.fastapp.dz` n'ont aucun enregistrement DNS, et
  // `help.fastdelivery.dz` répond 525 (handshake SSL). Le bon domaine du centre
  // d'aide est `help.fastapp.dz` ; `go.fastapp.dz` porte les redirections
  // courtes. Vérifié : les quatre répondent 200.
  const LEGAL_LINKS = [
    { label: t.footer.legalNotice, href: "https://help.fastapp.dz/hc/fast-delivery/articles/1720693983-mentions-legales" },
    { label: t.footer.terms, href: "https://go.fastapp.dz/cgu" },
    { label: t.footer.termsPro, href: "https://help.fastapp.dz/hc/fast-delivery/articles/1720689431-conditions-generales-d-utilisation" },
    { label: t.footer.privacy, href: "https://go.fastapp.dz/privacy" },
  ];

  // No footer during checkout — it's a focused flow.
  if (pathname?.startsWith("/cart")) return null;

  return (
    <footer className="border-t border-gray-200 bg-white dark:bg-[#1E1E1E] dark:border-[#313131] mt-auto">
      {/* Main columns + badges on the right */}
      <div className=" mx-auto px-6 py-10 flex flex-col gap-10 lg:flex-row lg:justify-center lg:gap-20">
        {/* Link columns */}
        <div className="flex flex-col gap-8 sm:flex-row sm:gap-16">
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <p className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">{col.title}</p>
              <ul className="space-y-2">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      className="text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 transition-colors"
                      target={l.href.startsWith("http") ? "_blank" : undefined}
                      rel={l.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* App store badges — vertical stack, right side */}
        <div className="flex flex-col items-start gap-3 lg:items-end lg:justify-start">
          <AppStoreButton lang={locale} />
          <GooglePlayButton lang={locale} />
        </div>
      </div>

      {/* Bottom bar */}
      <div className="max-w-7xl mx-auto px-6 pt-4 pb-20 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 dark:text-gray-500">
          <span>{t.footer.copyright}</span>
          {LEGAL_LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-400 dark:text-gray-400 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>

        {/* Social icons */}
        <div className="flex items-center gap-3">
          <a href="https://facebook.com/fast.algerie" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-400 dark:text-gray-400 transition-colors">
<FaFacebookF />

          </a>
          <a href="https://www.linkedin.com/company/71801112/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-400 dark:text-gray-400 transition-colors">
<FaLinkedinIn />
          </a>
          <a href="https://instagram.com/fast.algerie" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-400 dark:text-gray-400 transition-colors">
 <FaInstagram />
          </a>
          <a href="https://tiktok.com/@fast.dz" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-400 dark:text-gray-400 transition-colors">
  <FaTiktok />
          </a>
        </div>
      </div>
    </footer>
  );
}

